import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { trackPageView } from 'lib/analytics';

/**
 * Reports a page view (see `trackPageView`) once per pathname.
 *
 * `App` uses it for every route under it, waiting for the visitor to be known
 * so the first view already carries role and user type. The routes that sit
 * beside `App` in the router (`/login`, `/kiosk/:token`, `/promos`,
 * `/auth/callback`) render `PageViewTracker` instead — without it they would
 * send no page view at all, and a kiosk's cut sheet events no `page_path`.
 * @param ready - Hold page views until this is true
 */
export function usePageViewTracking(ready = true): void {
  const location = useLocation();
  const params = useParams();
  // `ready` can flicker around sign-in; don't count one page twice.
  const lastTrackedPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready) return;
    if (lastTrackedPathRef.current === location.pathname) return;
    lastTrackedPathRef.current = location.pathname;
    trackPageView(location.pathname, params);
    // Once per navigation; params change only when the path does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, location.pathname]);
}

/** Page views for a route rendered outside `App`. */
export function PageViewTracker(): null {
  usePageViewTracking();
  return null;
}
